/**
 * "You are about to be signed out."
 *
 * Shown for the last minute of the inactivity window. Without it, someone who stepped
 * away from a half-written shift correction comes back to a sign-in page and no idea
 * why — and the honest reading of that is "the system logged me out and lost my work".
 *
 * Dismissing it is the same action as any other interaction: it restarts the clock.
 */

import { useAuth } from './AuthProvider';
import { useI18n } from '@/i18n';

export function IdleWarning() {
  const { idleWarning, keepAlive, signOut } = useAuth();
  const { t } = useI18n();

  if (idleWarning === null) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="idle-title"
      className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center bg-black/50"
    >
      <div className="w-full max-w-sm rounded-xl border border-border bg-surface p-5 shadow-lg">
        <h2 id="idle-title" className="text-base font-bold">
          {t('auth.session.expiring')}
        </h2>
        <p className="mt-1.5 text-sm text-muted">
          {t('auth.session.expiringBody', { seconds: idleWarning })}
        </p>

        <div className="mt-4 flex gap-2.5">
          <button
            type="button"
            onClick={() => void signOut(null)}
            className="h-10 flex-1 rounded-lg border border-border text-sm font-semibold text-muted transition-colors hover:text-text"
          >
            {t('auth.signOut')}
          </button>
          <button
            type="button"
            onClick={keepAlive}
            autoFocus
            className="h-10 flex-1 rounded-lg bg-primary text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            {t('auth.session.stay')}
          </button>
        </div>
      </div>
    </div>
  );
}
